import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAppStore } from '../store';
import { uploadImage, validateImageFile, createImagePreview, revokeImagePreview } from '../services/storage.service';
import * as contentPagesService from '../services/rtdb/contentPages.service';
import { sendPasswordResetLink } from '../services/auth.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2, Calendar as CalendarIcon, Users as UsersIcon, Banknote, Clock, Mail, Check, X } from 'lucide-react';
import type { HolidayHome, BlockedDateRange, ContentPageType, SupportedLanguage } from '../types';

const FamilyBookingInfoEditor: React.FC = () => {
  const { familyBookingInfo, saveFamilyBookingInfo } = useAppStore();
  const [draft, setDraft] = useState(familyBookingInfo);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft(familyBookingInfo); }, [familyBookingInfo]);

  const handleSave = async () => {
    setSaving(true);
    await saveFamilyBookingInfo(draft);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="mt-8 space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Familien-Buchungsinfo</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Dieser Text erscheint nach einer Buchung nur für Eigentümer und Familienmitglieder (nicht für Gäste). Markdown wird unterstützt.
        </p>
      </div>
      <Tabs value={preview ? 'preview' : 'edit'} onValueChange={(v) => setPreview(v === 'preview')}>
        <TabsList>
          <TabsTrigger value="edit">Bearbeiten</TabsTrigger>
          <TabsTrigger value="preview">Vorschau</TabsTrigger>
        </TabsList>
        <TabsContent value="edit" className="mt-4">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="z.B. Bettwäsche befindet sich im Schrank im Flur. Bitte Handtücher selbst mitbringen..."
            className="min-h-[200px] font-mono text-sm"
          />
        </TabsContent>
        <TabsContent value="preview" className="mt-4">
          <div className="min-h-[200px] p-4 border rounded-lg bg-card">
            {draft ? (
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{draft}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Noch kein Text eingegeben.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Wird gespeichert...' : 'Speichern'}
        </Button>
        {saved && <span className="text-sm text-green-600">Gespeichert!</span>}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const {
    homes,
    bookings,
    users,
    contentPages,
    accessRequests,
    invitations,
    currentUser,
    addHome,
    updateHome,
    deleteHome,
    addUser,
    updateBooking,
    updateUser,
    deleteUser,
    approveAccessRequest,
    rejectAccessRequest,
    deleteAccessRequest,
    createInvitation,
    cancelInvitation,
    initializeContentPages
  } = useAppStore();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'overview' | 'homes' | 'bookings' | 'users' | 'requests' | 'content'>('overview');
  const [requestRoles, setRequestRoles] = useState<Record<string, 'family' | 'familyMember' | 'guest'>>({});
  const [requestHomeIds, setRequestHomeIds] = useState<Record<string, string[]>>({});
  const [requestProcessing, setRequestProcessing] = useState<Record<string, boolean>>({});

  // Content editing state
  const [activeContentType, setActiveContentType] = useState<ContentPageType>('arrival-departure');
  const [contentLanguage, setContentLanguage] = useState<SupportedLanguage>('en');
  const [contentDraft, setContentDraft] = useState('');
  const [showContentPreview, setShowContentPreview] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [showAddHomeModal, setShowAddHomeModal] = useState(false);
  const [showDeleteHomeModal, setShowDeleteHomeModal] = useState(false);
  const [editingHome, setEditingHome] = useState<HolidayHome | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<{ id: string; name: string; email: string; isAdmin: boolean; isFamily: boolean; isFamilyMember: boolean; status: string; allowedHomeIds: string[] } | null>(null);
  const [priceOverrides, setPriceOverrides] = useState<Record<string, string>>({});
  const [sendingLinkTo, setSendingLinkTo] = useState<Set<string>>(new Set());
  const [newHome, setNewHome] = useState({
    name: '',
    category: '',
    description: '',
    shortDescription: '',
    images: [''],
    highSeasonRate: 0,
    lowSeasonRate: 0,
    checkInInstructions: '',
    roomDescription: '',
    // Room layout fields
    totalBedrooms: 0,
    totalBeds: 0,
    totalBathrooms: 0,
    maxGuests: 0,

  });
  const [homeImageFile, setHomeImageFile] = useState<File | null>(null);
  const [homeImagePreview, setHomeImagePreview] = useState<string | null>(null);
  const [isUploadingHome, setIsUploadingHome] = useState(false);
  const [isDeletingHome, setIsDeletingHome] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availabilityHome, setAvailabilityHome] = useState<HolidayHome | null>(null);
  const [newBlockedDate, setNewBlockedDate] = useState({
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    isAdmin: false,
    isFamily: false,
    isFamilyMember: false,
    referredBy: 'none',
    allowedHomeIds: [] as string[],
  });

  // Invitation form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'family' | 'familyMember' | 'guest'>('guest');
  const [inviteCreating, setInviteCreating] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [generatedInviteToken, setGeneratedInviteToken] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Admin check
  if (!currentUser?.isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-24">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-2xl font-semibold mb-2">Zugang verweigert</h3>
          <p className="text-muted-foreground">Du benötigst Administratorrechte, um diese Seite zu sehen.</p>
        </div>
      </div>
    );
  }

  const handleAddHome = async () => {
    if (!newHome.name || !newHome.description || !newHome.highSeasonRate || !newHome.lowSeasonRate) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    setIsUploadingHome(true);

    try {
      let imageUrls: string[] = [];

      // Upload image if file is selected
      if (homeImageFile) {
        const uploadedUrl = await uploadImage(homeImageFile, 'holiday-homes');
        imageUrls = [uploadedUrl];
      } else if (newHome.images[0]?.trim()) {
        // Fall back to URL if provided
        imageUrls = [newHome.images[0]];
      }

      const homeData: Omit<HolidayHome, 'id'> = {
        name: newHome.name,
        category: newHome.category || undefined,
        description: newHome.description,
        shortDescription: newHome.shortDescription || undefined,
        images: imageUrls,
        availability: [],
        pricingStructure: {
          season: 'warm',
          weekdayRate: newHome.highSeasonRate,
          weekendRate: newHome.highSeasonRate,
          combinedRate: newHome.highSeasonRate,
          userTypeOverrides: {
            family: newHome.highSeasonRate,
            guest: newHome.highSeasonRate,
          },
          seasonalConfig: {
            residenceType: 'summer',
            highSeasonRanges: [
              {
                id: 'high-default',
                startMonth: 7,
                startDay: 1,
                endMonth: 8,
                endDay: 31,
                label: { de: 'Hauptsaison', en: 'High Season' },
              },
            ],
            highSeasonRates: {
              weekdayRate: newHome.highSeasonRate,
              weekendRate: newHome.highSeasonRate,
              weekRate: newHome.highSeasonRate,
              familyRate: newHome.highSeasonRate,
              guestRate: newHome.highSeasonRate,
            },
            lowSeasonRanges: [
              {
                id: 'low-default',
                startMonth: 4,
                startDay: 1,
                endMonth: 6,
                endDay: 30,
                label: { de: 'Nebensaison', en: 'Low Season' },
              },
            ],
            lowSeasonRates: {
              weekdayRate: newHome.lowSeasonRate,
              weekendRate: newHome.lowSeasonRate,
              weekRate: newHome.lowSeasonRate,
              familyRate: newHome.lowSeasonRate,
              guestRate: newHome.lowSeasonRate,
            },
          },
        },
        checkInInstructions: newHome.checkInInstructions || undefined,
        roomDescription: newHome.roomDescription || undefined,
        layout: newHome.totalBedrooms > 0 || newHome.totalBeds > 0 ? {
          totalRooms: newHome.totalBedrooms,
          totalBedrooms: newHome.totalBedrooms,
          totalBeds: newHome.totalBeds,
          totalBathrooms: newHome.totalBathrooms,
          maxGuests: newHome.maxGuests,
          rooms: [],
        } : undefined,
      };

      await addHome(homeData);

      // Reset form
      setNewHome({
        name: '',
        category: '',
        description: '',
        shortDescription: '',
        images: [''],
        highSeasonRate: 0,
        lowSeasonRate: 0,
        checkInInstructions: '',
        roomDescription: '',
        totalBedrooms: 0,
        totalBeds: 0,
        totalBathrooms: 0,
        maxGuests: 0,
      });
      setHomeImageFile(null);
      if (homeImagePreview) {
        revokeImagePreview(homeImagePreview);
        setHomeImagePreview(null);
      }
      setShowAddHomeModal(false);
    } catch (error) {
      console.error('Error adding home:', error);
      alert('Unterkunft konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
    } finally {
      setIsUploadingHome(false);
    }
  };

  const handleHomeImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Clean up previous preview
    if (homeImagePreview) {
      revokeImagePreview(homeImagePreview);
    }

    setHomeImageFile(file);
    setHomeImagePreview(createImagePreview(file));
  };

  const handleOpenDeleteHome = (home: HolidayHome) => {
    setEditingHome(home);
    setShowDeleteHomeModal(true);
  };

  const handleDeleteHome = async () => {
    if (!editingHome) return;

    setIsDeletingHome(true);

    try {
      await deleteHome(editingHome.id);
      setEditingHome(null);
      setShowDeleteHomeModal(false);
    } catch (error) {
      console.error('Error deleting home:', error);
      alert('Unterkunft konnte nicht gelöscht werden. Bitte erneut versuchen.');
    } finally {
      setIsDeletingHome(false);
    }
  };

  const handleOpenAvailability = (home: HolidayHome) => {
    setAvailabilityHome(home);
    setNewBlockedDate({ startDate: '', endDate: '', reason: '' });
    setShowAvailabilityModal(true);
  };

  const handleAddBlockedDate = async () => {
    if (!availabilityHome || !newBlockedDate.startDate || !newBlockedDate.endDate) {
      alert('Bitte Start- und Enddatum ausfüllen');
      return;
    }

    try {
      const newBlockedRange: BlockedDateRange = {
        startDate: new Date(newBlockedDate.startDate),
        endDate: new Date(newBlockedDate.endDate),
        reason: newBlockedDate.reason || 'Blocked',
      };

      const updatedAvailability = [...(availabilityHome.availability || []), newBlockedRange];
      await updateHome(availabilityHome.id, { availability: updatedAvailability });

      // Update local state
      setAvailabilityHome({
        ...availabilityHome,
        availability: updatedAvailability,
      });

      setNewBlockedDate({ startDate: '', endDate: '', reason: '' });
    } catch (error) {
      console.error('Error adding blocked date:', error);
      alert('Gesperrter Zeitraum konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
    }
  };

  const handleRemoveBlockedDate = async (index: number) => {
    if (!availabilityHome) return;

    try {
      const updatedAvailability = availabilityHome.availability.filter((_, i) => i !== index);
      await updateHome(availabilityHome.id, { availability: updatedAvailability });

      // Update local state
      setAvailabilityHome({
        ...availabilityHome,
        availability: updatedAvailability,
      });
    } catch (error) {
      console.error('Error removing blocked date:', error);
      alert('Gesperrter Zeitraum konnte nicht entfernt werden. Bitte erneut versuchen.');
    }
  };

  const handleOpenEditUser = (user: typeof users[0]) => {
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      isAdmin: user.isAdmin,
      isFamily: user.isFamily,
      isFamilyMember: user.isFamilyMember ?? false,
      status: user.status,
      allowedHomeIds: user.allowedHomeIds ?? [],
    });
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      await updateUser(editingUser.id, {
        name: editingUser.name,
        isAdmin: editingUser.isAdmin,
        isFamily: editingUser.isFamily,
        isFamilyMember: editingUser.isFamilyMember,
        status: editingUser.status as 'pending' | 'approved' | 'blocked',
        allowedHomeIds: editingUser.allowedHomeIds,
      });
      setEditingUser(null);
      setShowEditUserModal(false);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Benutzer konnte nicht aktualisiert werden. Bitte erneut versuchen.');
    }
  };

  const handleApproveBooking = async (bookingId: string) => {
    try {
      const updates: { status: 'approved'; totalPrice?: number } = { status: 'approved' };
      const override = priceOverrides[bookingId];
      if (override !== undefined && override !== '') {
        const parsed = parseFloat(override);
        if (!isNaN(parsed)) updates.totalPrice = parsed;
      }
      await updateBooking(bookingId, updates);
      setPriceOverrides(prev => { const next = { ...prev }; delete next[bookingId]; return next; });
    } catch (error) {
      console.error('Error approving booking:', error);
      alert('Buchung konnte nicht genehmigt werden. Bitte erneut versuchen.');
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    try {
      const updates: { status: 'cancelled'; totalPrice?: number } = { status: 'cancelled' };
      const override = priceOverrides[bookingId];
      if (override !== undefined && override !== '') {
        const parsed = parseFloat(override);
        if (!isNaN(parsed)) updates.totalPrice = parsed;
      }
      await updateBooking(bookingId, updates);
      setPriceOverrides(prev => { const next = { ...prev }; delete next[bookingId]; return next; });
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Buchung konnte nicht abgelehnt werden. Bitte erneut versuchen.');
    }
  };

  const buildInviteLink = (token: string) => `${window.location.origin}/invite/${token}`;

  const handleCreateInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;

    setInviteCreating(true);
    setInviteError(null);
    setGeneratedInviteToken(null);

    const result = await createInvitation(email, inviteRole);
    if (result.success && result.token) {
      setGeneratedInviteToken(result.token);
      setInviteEmail('');
      setInviteRole('guest');
    } else {
      setInviteError(result.error || 'Einladung konnte nicht erstellt werden.');
    }
    setInviteCreating(false);
  };

  const handleCopyInviteLink = async (token: string) => {
    try {
      await navigator.clipboard.writeText(buildInviteLink(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      console.error('Clipboard error:', err);
    }
  };

  const handleCancelInvite = async (invitationId: string) => {
    if (!confirm('Diese Einladung wirklich zurückziehen?')) return;
    await cancelInvitation(invitationId);
  };

  const handleApproveUser = async (userId: string) => {
    try {
      await updateUser(userId, { status: 'approved' });
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Benutzer konnte nicht genehmigt werden. Bitte erneut versuchen.');
    }
  };

  const handleBlockUser = async (userId: string) => {
    if (!confirm('Möchtest du diesen Benutzer wirklich sperren? Er wird sofort abgemeldet.')) {
      return;
    }

    try {
      await updateUser(userId, { status: 'blocked' });
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Benutzer konnte nicht gesperrt werden. Bitte erneut versuchen.');
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Benutzer "${userName}" wirklich endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    try {
      await deleteUser(userId);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Benutzer konnte nicht gelöscht werden.');
    }
  };

  const handleSendPasswordLink = async (userId: string, email: string) => {
    if (!confirm(`Einen Link zum Festlegen des Passworts an ${email} senden?`)) {
      return;
    }
    setSendingLinkTo(prev => new Set(prev).add(userId));
    try {
      await sendPasswordResetLink(email);
      alert(`Link an ${email} gesendet. Er ist eine Stunde gültig.`);
    } catch (error) {
      console.error('Error sending password link:', error);
      alert(error instanceof Error ? error.message : 'Link konnte nicht gesendet werden. Bitte erneut versuchen.');
    } finally {
      setSendingLinkTo(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      await addUser({
        name: newUser.name,
        email: newUser.email.toLowerCase(),
        isAdmin: newUser.isAdmin,
        isFamily: newUser.isFamily,
        isFamilyMember: newUser.isFamilyMember,
        referredBy: (newUser.referredBy && newUser.referredBy !== 'none') ? newUser.referredBy : (currentUser?.id || ''),
        status: 'approved',
        allowedHomeIds: newUser.allowedHomeIds,
      });

      setNewUser({
        name: '',
        email: '',
        isAdmin: false,
        isFamily: false,
        isFamilyMember: false,
        referredBy: '',
        allowedHomeIds: [],
      });
      setShowAddUserModal(false);
    } catch (error) {
      console.error('Error adding user:', error);
      alert('Benutzer konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
    }
  };

  const stats = [
    { label: 'Unterkünfte', value: homes.length },
    { label: 'Buchungen', value: bookings.length },
    { label: 'Ausstehende Genehmigungen', value: bookings.filter(b => b.status === 'pending').length },
    { label: 'Aktive Benutzer', value: users.filter(u => u.status === 'approved').length },
  ];

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const pendingUsers = users.filter(u => u.status === 'pending');

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'pending': return 'secondary';
      case 'cancelled': return 'destructive';
      case 'blocked': return 'destructive';
      default: return 'outline';
    }
  };

  const pendingRequests = accessRequests.filter(r => r.status === 'pending');

  const tabs = [
    { id: 'overview', label: 'Übersicht', pending: 0 },
    { id: 'homes', label: 'Unterkünfte', pending: 0 },
    { id: 'bookings', label: 'Buchungen', pending: pendingBookings.length },
    { id: 'users', label: 'Benutzer', pending: pendingUsers.length },
    { id: 'requests', label: 'Anfragen', pending: pendingRequests.length },
    { id: 'content', label: 'Inhalte', pending: 0 },
  ] as const;

  const totalPending = pendingBookings.length + pendingUsers.length + pendingRequests.length;

  // Content page types for the admin
  const contentTypes: { id: ContentPageType; label: string }[] = [
    { id: 'arrival-departure', label: 'An- & Abreise' },
    { id: 'house-rules', label: 'Hausordnung' },
    { id: 'payment-terms', label: 'Zahlungsmodalitäten' },
    { id: 'pricing-info', label: 'Preise' },
  ];

  // Initialize content pages on mount
  useEffect(() => {
    if (currentUser?.isAdmin) {
      initializeContentPages();
    }
  }, [currentUser?.isAdmin, initializeContentPages]);

  // Load content draft when switching pages or language
  useEffect(() => {
    const page = contentPages.find(p => p.type === activeContentType);
    if (page) {
      setContentDraft(page.content[contentLanguage] || '');
    } else {
      setContentDraft('');
    }
  }, [activeContentType, contentLanguage, contentPages]);

  const handleSaveContent = async () => {
    const page = contentPages.find(p => p.type === activeContentType);
    if (!page) return;

    setIsSavingContent(true);
    try {
      const updatedContent = {
        ...page.content,
        [contentLanguage]: contentDraft,
      };
      await contentPagesService.updateContentPage(page.id, {
        content: updatedContent,
        lastUpdatedAt: new Date(),
        lastUpdatedBy: currentUser?.id || '',
      });
    } catch (error) {
      console.error('Error saving content:', error);
    }
    setIsSavingContent(false);
  };

  const handleTogglePublish = async () => {
    const page = contentPages.find(p => p.type === activeContentType);
    if (!page) return;

    try {
      await contentPagesService.updateContentPage(page.id, {
        isPublished: !page.isPublished,
        lastUpdatedAt: new Date(),
        lastUpdatedBy: currentUser?.id || '',
      });
    } catch (error) {
      console.error('Error toggling publish status:', error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-muted-foreground">Unterkünfte, Buchungen und Benutzer verwalten</p>
      </div>

      {/* Action Required Banner */}
      {totalPending > 0 && activeTab === 'overview' && (
        <div className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800/50 flex items-start gap-3">
          <div className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400/30 text-amber-700 dark:text-amber-400">
            <span className="font-bold text-sm">{totalPending > 99 ? '99+' : totalPending}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900 dark:text-amber-200">
              {totalPending === 1 ? 'Ein Eintrag wartet auf deine Bestätigung' : `${totalPending} Einträge warten auf deine Bestätigung`}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-amber-800 dark:text-amber-300">
              {pendingBookings.length > 0 && (
                <button onClick={() => setActiveTab('bookings')} className="hover:underline">
                  {pendingBookings.length} {pendingBookings.length === 1 ? 'Buchung' : 'Buchungen'}
                </button>
              )}
              {pendingRequests.length > 0 && (
                <button onClick={() => setActiveTab('requests')} className="hover:underline">
                  {pendingRequests.length} {pendingRequests.length === 1 ? 'Anfrage' : 'Anfragen'}
                </button>
              )}
              {pendingUsers.length > 0 && (
                <button onClick={() => setActiveTab('users')} className="hover:underline">
                  {pendingUsers.length} Benutzer
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="mb-8">
        <TabsList className="w-full flex-wrap h-auto gap-1 p-1">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="flex-1 min-w-[auto] gap-1.5">
              {tab.label}
              {tab.pending > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                  {tab.pending > 99 ? '99+' : tab.pending}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {stats.map((stat, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow hover:-translate-y-0.5">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-primary mb-1">{stat.value}</div>
                  <div className="text-xs uppercase font-medium tracking-wider text-muted-foreground">
                    {stat.label}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pending Bookings */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Ausstehende Buchungen</h2>
                {pendingBookings.length > 3 && (
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('bookings')}>
                    Alle {pendingBookings.length} anzeigen
                  </Button>
                )}
              </div>
              {pendingBookings.length > 0 ? (
                <div className="space-y-4">
                  {pendingBookings.slice(0, 3).map(booking => {
                    const home = homes.find(h => h.id === booking.homeId);
                    const user = users.find(u => u.id === booking.userId);
                    const start = new Date(booking.startDate);
                    const end = new Date(booking.endDate);
                    const nights = Math.max(0, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
                    const dateFmt = (d: Date) => d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return (
                      <Card key={booking.id} className="hover:shadow-md transition-shadow border-l-4 border-l-amber-400">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex justify-between items-start gap-3">
                            <div className="min-w-0 flex-1">
                              <h3 className="font-semibold truncate">{home?.name || 'Unbekannte Unterkunft'}</h3>
                              <p className="text-sm text-muted-foreground truncate">
                                {user?.name || 'Unbekannter Benutzer'}
                                {user?.email && <span className="text-xs"> · {user.email}</span>}
                              </p>
                            </div>
                            <Badge variant="outline" className="font-mono text-[10px] shrink-0">
                              {booking.referenceNumber}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{dateFmt(start)} → {dateFmt(end)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Clock className="h-3.5 w-3.5 shrink-0" />
                              <span>{nights} {nights === 1 ? 'Nacht' : 'Nächte'}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <UsersIcon className="h-3.5 w-3.5 shrink-0" />
                              <span>{booking.guests ?? 1} {(booking.guests ?? 1) === 1 ? 'Person' : 'Personen'}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Banknote className="h-3.5 w-3.5 shrink-0" />
                              <span className="font-medium text-foreground">
                                {booking.totalPrice === 0 ? 'Kostenlos' : `CHF ${booking.totalPrice.toLocaleString('de-CH')}`}
                              </span>
                            </div>
                          </div>

                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm"
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApproveBooking(booking.id)}
                            >
                              <Check className="h-3.5 w-3.5 mr-1" />
                              Genehmigen
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleCancelBooking(booking.id)}
                            >
                              <X className="h-3.5 w-3.5 mr-1" />
                              Ablehnen
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    Keine ausstehenden Buchungen
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Pending Access Requests */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Ausstehende Zugangsanfragen</h2>
                {pendingRequests.length > 3 && (
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('requests')}>
                    Alle {pendingRequests.length} anzeigen
                  </Button>
                )}
              </div>
              {pendingRequests.length > 0 ? (
                <div className="space-y-4">
                  {pendingRequests.slice(0, 3).map(req => {
                    const created = new Date(req.createdAt);
                    return (
                      <Card key={req.id} className="hover:shadow-md transition-shadow border-l-4 border-l-blue-400">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start gap-2">
                            <Mail className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold truncate">{req.email}</p>
                              {req.message && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">„{req.message}"</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                Angefragt am {created.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            onClick={() => setActiveTab('requests')}
                          >
                            Prüfen & entscheiden
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    Keine ausstehenden Anfragen
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Pending Users (only shown when any exist) */}
          {pendingUsers.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Ausstehende Benutzer</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingUsers.slice(0, 6).map(user => (
                  <Card key={user.id} className="hover:shadow-md transition-shadow border-l-4 border-l-orange-400">
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-1 truncate">{user.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3 truncate">{user.email}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleApproveUser(user.id)}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />
                          Genehmigen
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleBlockUser(user.id)}
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          Sperren
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Properties Tab */}
      {activeTab === 'homes' && (
        <>
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h2 className="text-xl font-semibold">Unterkunftsverwaltung</h2>
            <Button onClick={() => navigate('/admin/properties/new')}>
              Unterkunft hinzufügen
            </Button>
          </div>

          <div className="space-y-4">
            {homes.map(home => (
              <Card key={home.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{home.name}</h3>
                      <Badge variant="secondary">
                        {home.pricingStructure.season === 'warm' ? 'Sommersaison' : 'Wintersaison'}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenAvailability(home)}
                      >
                        Verfügbarkeit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/properties/${home.id}/edit`)}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleOpenDeleteHome(home)}
                      >
                        Löschen
                      </Button>
                    </div>
                  </div>

                  {home.images.length > 0 && (
                    <img
                      src={home.images[0]}
                      alt={home.name}
                      className="w-full h-48 object-cover rounded-md mb-4"
                    />
                  )}

                  <p className="text-muted-foreground mb-4">{home.description}</p>

                  {(() => {
                    const sc = home.pricingStructure.seasonalConfig as any;
                    const high = Array.isArray(sc?.seasons) ? sc.seasons.find((s: any) => s.type === 'high') : null;
                    const low = Array.isArray(sc?.seasons) ? sc.seasons.find((s: any) => s.type === 'low') : null;
                    return (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-muted rounded-md">
                          <div className="text-lg font-semibold text-primary">
                            CHF {high?.rates?.weekRate ?? home.pricingStructure.combinedRate ?? home.pricingStructure.weekdayRate}
                          </div>
                          <div className="text-xs text-muted-foreground">Hochsaison / Nacht</div>
                        </div>
                        <div className="text-center p-3 bg-muted rounded-md">
                          <div className="text-lg font-semibold text-primary">
                            CHF {low?.rates?.weekRate ?? home.pricingStructure.combinedRate ?? home.pricingStructure.weekdayRate}
                          </div>
                          <div className="text-xs text-muted-foreground">Nebensaison / Nacht</div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <>
          <h2 className="text-xl font-semibold mb-6">Buchungsverwaltung</h2>

          <div className="space-y-4">
            {bookings.map(booking => {
              const home = homes.find(h => h.id === booking.homeId);
              const user = users.find(u => u.id === booking.userId);
              return (
                <Card key={booking.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold">{home?.name || 'Unbekannte Unterkunft'}</h3>
                        <p className="text-sm text-muted-foreground">
                          Gebucht von {user?.name || 'Unbekannter Benutzer'}
                        </p>
                      </div>
                      <Badge variant={getStatusVariant(booking.status)}>
                        {booking.status}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                          Anreise
                        </div>
                        <div className="text-sm font-medium">
                          {booking.startDate.toLocaleDateString('de-DE')}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                          Abreise
                        </div>
                        <div className="text-sm font-medium">
                          {booking.endDate.toLocaleDateString('de-DE')}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                          Gesamtpreis
                        </div>
                        {booking.status === 'pending' ? (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-sm text-muted-foreground">CHF</span>
                            <Input
                              type="number"
                              min={0}
                              className="h-7 w-24 text-sm"
                              value={priceOverrides[booking.id] ?? booking.totalPrice}
                              onChange={e => setPriceOverrides(prev => ({ ...prev, [booking.id]: e.target.value }))}
                            />
                          </div>
                        ) : (
                          <div className="text-sm font-medium">CHF {booking.totalPrice}</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                          Zahlung
                        </div>
                        <div className="text-sm font-medium">
                          <Button
                            variant={booking.isPaid ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateBooking(booking.id, { isPaid: !booking.isPaid })}
                            className={booking.isPaid ? 'bg-green-600 hover:bg-green-700' : ''}
                          >
                            {booking.isPaid ? 'Bezahlt' : 'Als bezahlt markieren'}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      {booking.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleApproveBooking(booking.id)}
                          >
                            Genehmigen
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelBooking(booking.id)}
                          >
                            Ablehnen
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Invitations */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-1">Einladungen</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Generiere einen Einladungslink und sende ihn der Person. Sie kann sich damit
                per Google oder E-Mail-Link registrieren und landet danach in der Freigabe-Warteschlange.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_auto] gap-3 items-end">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">E-Mail-Adresse</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="name@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-role">Rolle</Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(v) => setInviteRole(v as 'family' | 'familyMember' | 'guest')}
                  >
                    <SelectTrigger id="invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="guest">Gast</SelectItem>
                      <SelectItem value="familyMember">Familienmitglied</SelectItem>
                      <SelectItem value="family">Eigentümer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleCreateInvite}
                  disabled={!inviteEmail.trim() || inviteCreating}
                >
                  {inviteCreating ? 'Wird erstellt…' : 'Link erstellen'}
                </Button>
              </div>

              {inviteError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {inviteError}
                </div>
              )}

              {generatedInviteToken && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-900 mb-2">
                    Einladungslink erstellt — jetzt kopieren und versenden:
                  </p>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={buildInviteLink(generatedInviteToken)}
                      className="font-mono text-xs"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="outline"
                      onClick={() => handleCopyInviteLink(generatedInviteToken)}
                    >
                      {copiedToken === generatedInviteToken ? 'Kopiert!' : 'Kopieren'}
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3"
                    onClick={() => setGeneratedInviteToken(null)}
                  >
                    Erledigt
                  </Button>
                </div>
              )}

              {/* Pending invitations list */}
              {invitations.filter(i => i.status === 'pending').length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Offene Einladungen
                  </h3>
                  <div className="space-y-2">
                    {invitations
                      .filter(i => i.status === 'pending')
                      .map(inv => {
                        const expired = new Date() > inv.expiresAt;
                        return (
                          <div
                            key={inv.id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border bg-card"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{inv.email}</p>
                              <p className="text-xs text-muted-foreground">
                                Rolle: {inv.role === 'family' ? 'Eigentümer' : inv.role === 'familyMember' ? 'Familienmitglied' : 'Gast'}
                                {' · '}
                                {expired
                                  ? <span className="text-red-600">abgelaufen</span>
                                  : `läuft ab am ${inv.expiresAt.toLocaleDateString()}`}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              {!expired && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCopyInviteLink(inv.token)}
                                >
                                  {copiedToken === inv.token ? 'Kopiert!' : 'Link kopieren'}
                                </Button>
                              )}
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleCancelInvite(inv.id)}
                              >
                                Zurückziehen
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h2 className="text-xl font-semibold">Benutzerverwaltung</h2>
            <Button onClick={() => setShowAddUserModal(true)}>
              Benutzer zur Whitelist hinzufügen
            </Button>
          </div>

          {users.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Keine Benutzer vorhanden.
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mobile: Card Layout */}
              <div className="md:hidden space-y-3">
                {users.map(user => {
                  const role = user.isAdmin ? 'Admin' : user.isFamily ? 'Eigentümer' : user.isFamilyMember ? 'Familienmitglied' : 'Gast';
                  return (
                    <Card key={user.id}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{user.name}</div>
                            <div className="text-sm text-muted-foreground truncate">{user.email}</div>
                          </div>
                          <Badge variant={getStatusVariant(user.status)} className="shrink-0">
                            {user.status === 'approved' ? 'Aktiv' : user.status === 'pending' ? 'Ausstehend' : 'Gesperrt'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span>Rolle: <span className="text-foreground font-medium">{role}</span></span>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-2 border-t">
                          <Button size="sm" variant="outline" onClick={() => handleOpenEditUser(user)}>
                            Bearbeiten
                          </Button>
                          {user.status !== 'blocked' && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={sendingLinkTo.has(user.id)}
                              onClick={() => handleSendPasswordLink(user.id, user.email)}
                            >
                              {sendingLinkTo.has(user.id) ? 'Wird gesendet…' : 'Passwort-Link senden'}
                            </Button>
                          )}
                          {user.status === 'pending' && (
                            <Button size="sm" variant="secondary" onClick={() => handleApproveUser(user.id)}>
                              Genehmigen
                            </Button>
                          )}
                          {user.status !== 'blocked' && user.id !== currentUser?.id && (
                            <Button size="sm" variant="destructive" onClick={() => handleBlockUser(user.id)}>
                              Sperren
                            </Button>
                          )}
                          {user.id !== currentUser?.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(user.id, user.name)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Desktop: Table Layout */}
              <Card className="hidden md:block">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>E-Mail</TableHead>
                        <TableHead>Rolle</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(user => {
                        const role = user.isAdmin ? 'Admin' : user.isFamily ? 'Eigentümer' : user.isFamilyMember ? 'Familienmitglied' : 'Gast';
                        return (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">{user.name}</TableCell>
                            <TableCell className="text-muted-foreground">{user.email}</TableCell>
                            <TableCell>{role}</TableCell>
                            <TableCell>
                              <Badge variant={getStatusVariant(user.status)}>
                                {user.status === 'approved' ? 'Aktiv' : user.status === 'pending' ? 'Ausstehend' : 'Gesperrt'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleOpenEditUser(user)}>
                                  Bearbeiten
                                </Button>
                                {user.status !== 'blocked' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={sendingLinkTo.has(user.id)}
                                    onClick={() => handleSendPasswordLink(user.id, user.email)}
                                  >
                                    {sendingLinkTo.has(user.id) ? 'Wird gesendet…' : 'Passwort-Link senden'}
                                  </Button>
                                )}
                                {user.status === 'pending' && (
                                  <Button size="sm" variant="secondary" onClick={() => handleApproveUser(user.id)}>
                                    Genehmigen
                                  </Button>
                                )}
                                {user.status !== 'blocked' && user.id !== currentUser?.id && (
                                  <Button size="sm" variant="destructive" onClick={() => handleBlockUser(user.id)}>
                                    Sperren
                                  </Button>
                                )}
                                {!user.isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteUser(user.id, user.name)}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Access Requests Tab */}
      {activeTab === 'requests' && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold">Zugriffsanfragen</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Personen, die über die Login-Seite Zugang angefragt haben.
              </p>
            </div>
          </div>

          {accessRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Keine Zugriffsanfragen vorhanden.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {['pending', 'approved', 'rejected'].map((status) => {
                const group = accessRequests.filter(r => r.status === status);
                if (group.length === 0) return null;
                const label = status === 'pending' ? 'Ausstehend' : status === 'approved' ? 'Freigeschaltet' : 'Abgelehnt';
                const color = status === 'pending' ? 'text-amber-600' : status === 'approved' ? 'text-green-600' : 'text-red-600';
                return (
                  <div key={status}>
                    <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${color}`}>{label}</h3>
                    <div className="space-y-3">
                      {group.map((req) => {
                        const selectedRole = requestRoles[req.id] || 'guest';
                        const selectedHomeIds = requestHomeIds[req.id] || [];
                        const isProcessing = requestProcessing[req.id];
                        return (
                          <Card key={req.id}>
                            <CardContent className="p-4">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="space-y-1">
                                  <p className="font-medium">{req.email}</p>
                                  {req.message && (
                                    <p className="text-sm text-muted-foreground italic">„{req.message}"</p>
                                  )}
                                  <p className="text-xs text-muted-foreground">
                                    {req.createdAt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    {req.approvedAt && ` · Freigeschaltet: ${req.approvedAt.toLocaleDateString('de-DE')}`}
                                  </p>
                                </div>
                                {status === 'pending' && (
                                  <div className="flex items-center gap-2 shrink-0">
                                    <select
                                      value={selectedRole}
                                      onChange={(e) => setRequestRoles(prev => ({ ...prev, [req.id]: e.target.value as any }))}
                                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                                    >
                                      <option value="family">Eigentümer</option>
                                      <option value="familyMember">Familienmitglied</option>
                                      <option value="guest">Gast</option>
                                    </select>
                                    <Button
                                      size="sm"
                                      disabled={isProcessing}
                                      onClick={async () => {
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: true }));
                                        await approveAccessRequest(req.id, selectedRole, selectedHomeIds);
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: false }));
                                        alert(`Zugang freigeschaltet! Ein Anmelde-Link wird an ${req.email} gesendet.`);
                                      }}
                                    >
                                      Freischalten
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={isProcessing}
                                      onClick={async () => {
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: true }));
                                        await rejectAccessRequest(req.id);
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: false }));
                                      }}
                                    >
                                      Ablehnen
                                    </Button>
                                  </div>
                                )}
                                {status === 'approved' && (
                                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                    <Badge variant="default" className="bg-green-100 text-green-800">
                                      {req.approvedRole === 'family' ? 'Eigentümer' : req.approvedRole === 'familyMember' ? 'Familienmitglied' : 'Gast'}
                                    </Badge>
                                    {req.approvedHomeIds && req.approvedHomeIds.length > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        {req.approvedHomeIds.map(hid => homes.find(h => h.id === hid)?.name).filter(Boolean).join(', ')}
                                      </span>
                                    )}
                                    {(!req.approvedHomeIds || req.approvedHomeIds.length === 0) && (
                                      <span className="text-xs text-muted-foreground">Alle Unterkünfte</span>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={isProcessing}
                                      onClick={async () => {
                                        if (!confirm(`Anfrage von ${req.email} und den zugehörigen Benutzer endgültig löschen?`)) return;
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: true }));
                                        const linkedUser = users.find(u => u.email.toLowerCase() === req.email.toLowerCase());
                                        if (linkedUser) await deleteUser(linkedUser.id);
                                        await deleteAccessRequest(req.id);
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: false }));
                                      }}
                                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                                {status === 'rejected' && (
                                  <div className="flex items-center gap-2 shrink-0">
                                    <Badge variant="destructive">Abgelehnt</Badge>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={isProcessing}
                                      onClick={async () => {
                                        if (!confirm(`Anfrage von ${req.email} endgültig löschen?`)) return;
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: true }));
                                        await deleteAccessRequest(req.id);
                                        setRequestProcessing(prev => ({ ...prev, [req.id]: false }));
                                      }}
                                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                                {status === 'pending' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={isProcessing}
                                    onClick={async () => {
                                      if (!confirm(`Anfrage von ${req.email} endgültig löschen?`)) return;
                                      setRequestProcessing(prev => ({ ...prev, [req.id]: true }));
                                      await deleteAccessRequest(req.id);
                                      setRequestProcessing(prev => ({ ...prev, [req.id]: false }));
                                    }}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </div>
                              {status === 'pending' && homes.length > 0 && (
                                <div className="mt-3 pt-3 border-t">
                                  <p className="text-xs font-medium text-muted-foreground mb-2">
                                    Sichtbare Unterkünfte {selectedHomeIds.length === 0 && <span className="text-xs font-normal">(keine Auswahl = alle)</span>}
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {homes.map((home) => {
                                      const checked = selectedHomeIds.includes(home.id);
                                      return (
                                        <label key={home.id} className="flex items-center gap-1.5 cursor-pointer text-sm">
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => {
                                              const next = checked
                                                ? selectedHomeIds.filter(id => id !== home.id)
                                                : [...selectedHomeIds, home.id];
                                              setRequestHomeIds(prev => ({ ...prev, [req.id]: next }));
                                            }}
                                            className="h-4 w-4 rounded border-input"
                                          />
                                          {home.name}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Inhaltsverwaltung</h2>
            <p className="text-muted-foreground">
              Inhalte für die Gastinformationsseiten bearbeiten. Markdown-Formatierung wird unterstützt.
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-6">
            {/* Content type selector */}
            <div className="space-y-2">
              <Label>Seite</Label>
              <div className="space-y-2">
                {contentTypes.map((type) => {
                  const page = contentPages.find(p => p.type === type.id);
                  return (
                    <button
                      key={type.id}
                      onClick={() => setActiveContentType(type.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                        activeContentType === type.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card hover:bg-accent border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{type.label}</span>
                        {page?.isPublished && (
                          <Badge variant="secondary" className="text-xs">Veröffentlicht</Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content editor */}
            <div className="lg:col-span-3 space-y-4">
              {/* Language toggle and actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Label>Sprache:</Label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={contentLanguage === 'en' ? 'default' : 'outline'}
                      onClick={() => setContentLanguage('en')}
                    >
                      Englisch
                    </Button>
                    <Button
                      size="sm"
                      variant={contentLanguage === 'de' ? 'default' : 'outline'}
                      onClick={() => setContentLanguage('de')}
                    >
                      Deutsch
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {(() => {
                    const page = contentPages.find(p => p.type === activeContentType);
                    return (
                      <div className="flex items-center gap-2">
                        <Label htmlFor="publish-toggle">Veröffentlicht</Label>
                        <Switch
                          id="publish-toggle"
                          checked={page?.isPublished || false}
                          onCheckedChange={handleTogglePublish}
                        />
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Editor/Preview tabs */}
              <Tabs value={showContentPreview ? 'preview' : 'edit'} onValueChange={(v) => setShowContentPreview(v === 'preview')}>
                <TabsList>
                  <TabsTrigger value="edit">Bearbeiten</TabsTrigger>
                  <TabsTrigger value="preview">Vorschau</TabsTrigger>
                </TabsList>

                <TabsContent value="edit" className="mt-4">
                  <Textarea
                    value={contentDraft}
                    onChange={(e) => setContentDraft(e.target.value)}
                    placeholder="Inhalt mit Markdown-Formatierung eingeben..."
                    className="min-h-[400px] font-mono text-sm"
                  />
                </TabsContent>

                <TabsContent value="preview" className="mt-4">
                  <div className="min-h-[400px] p-4 border rounded-lg bg-card">
                    {contentDraft ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown>{contentDraft}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Kein Inhalt zur Vorschau</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Save button */}
              <div className="flex justify-end gap-2">
                <Button
                  onClick={handleSaveContent}
                  disabled={isSavingContent}
                >
                  {isSavingContent ? 'Wird gespeichert...' : 'Inhalt speichern'}
                </Button>
              </div>

              {/* Last updated info */}
              {(() => {
                const page = contentPages.find(p => p.type === activeContentType);
                if (page?.lastUpdatedAt) {
                  return (
                    <p className="text-xs text-muted-foreground">
                      Zuletzt aktualisiert: {new Date(page.lastUpdatedAt).toLocaleString('de-DE')}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </>
      )}

      {/* Family Booking Info Editor */}
      {currentUser?.isAdmin && <FamilyBookingInfoEditor />}

      {/* Add Home Modal */}
      <Dialog open={showAddHomeModal} onOpenChange={setShowAddHomeModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Neue Unterkunft hinzufügen</DialogTitle>
            <DialogDescription>
              Eine neue Ferienunterkunft erstellen
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name der Unterkunft</Label>
              <Input
                id="name"
                value={newHome.name}
                onChange={(e) => setNewHome({ ...newHome, name: e.target.value })}
                placeholder="Name der Unterkunft eingeben"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Kategorie <span className="text-muted-foreground text-xs">(z.B. Ferienhaus, Chalet, Bauernhof)</span></Label>
              <Input
                id="category"
                value={newHome.category}
                onChange={(e) => setNewHome({ ...newHome, category: e.target.value })}
                placeholder="z.B. Ferienhaus"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shortDescription">Kurzbeschreibung <span className="text-muted-foreground text-xs">(Kartenansicht)</span></Label>
              <Input
                id="shortDescription"
                value={newHome.shortDescription}
                onChange={(e) => setNewHome({ ...newHome, shortDescription: e.target.value })}
                placeholder="z.B. Direkt am See, 5 km vom Dorf. Grosser Garten mit Seeblick."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <textarea
                id="description"
                value={newHome.description}
                onChange={(e) => setNewHome({ ...newHome, description: e.target.value })}
                placeholder="Unterkunft beschreiben..."
                className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="roomDescription">Zimmerbeschreibung</Label>
              <Input
                id="roomDescription"
                value={newHome.roomDescription}
                onChange={(e) => setNewHome({ ...newHome, roomDescription: e.target.value })}
                placeholder="z.B. 3 Schlafzimmer, Wohnzimmer, Küche, 2 Bäder"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="highSeasonRate">Hochsaison Nachtpreis (CHF)</Label>
                <Input
                  id="highSeasonRate"
                  type="number"
                  value={newHome.highSeasonRate || ''}
                  onChange={(e) => setNewHome({ ...newHome, highSeasonRate: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lowSeasonRate">Nebensaison Nachtpreis (CHF)</Label>
                <Input
                  id="lowSeasonRate"
                  type="number"
                  value={newHome.lowSeasonRate || ''}
                  onChange={(e) => setNewHome({ ...newHome, lowSeasonRate: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="checkInInstructions">Check-in Hinweise</Label>
              <textarea
                id="checkInInstructions"
                value={newHome.checkInInstructions}
                onChange={(e) => setNewHome({ ...newHome, checkInInstructions: e.target.value })}
                placeholder="Check-in Hinweise, Zugangscodes, WLAN-Passwort, etc. eingeben..."
                className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              />
              <p className="text-xs text-muted-foreground">
                Wird Gästen nach Genehmigung ihrer Buchung angezeigt
              </p>
            </div>

            {/* Room Layout Section */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium">Zimmeraufteilung</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="totalBedrooms">Schlafzimmer</Label>
                  <Input
                    id="totalBedrooms"
                    type="number"
                    min="0"
                    value={newHome.totalBedrooms || ''}
                    onChange={(e) => setNewHome({ ...newHome, totalBedrooms: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalBeds">Betten</Label>
                  <Input
                    id="totalBeds"
                    type="number"
                    min="0"
                    value={newHome.totalBeds || ''}
                    onChange={(e) => setNewHome({ ...newHome, totalBeds: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalBathrooms">Badezimmer</Label>
                  <Input
                    id="totalBathrooms"
                    type="number"
                    min="0"
                    value={newHome.totalBathrooms || ''}
                    onChange={(e) => setNewHome({ ...newHome, totalBathrooms: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxGuests">Max. Gäste</Label>
                  <Input
                    id="maxGuests"
                    type="number"
                    min="0"
                    value={newHome.maxGuests || ''}
                    onChange={(e) => setNewHome({ ...newHome, maxGuests: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="homeImage">Bild der Unterkunft</Label>
              <Input
                id="homeImage"
                type="file"
                accept="image/*"
                onChange={handleHomeImageChange}
                className="cursor-pointer"
              />
              <p className="text-xs text-muted-foreground">
                Bild hochladen (max. 5 MB, JPEG/PNG/WebP)
              </p>
              {homeImagePreview && (
                <div className="mt-2">
                  <img
                    src={homeImagePreview}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-md border"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="imageUrl">Oder Bild-URL einfügen</Label>
              <Input
                id="imageUrl"
                type="url"
                value={newHome.images[0]}
                onChange={(e) => setNewHome({ ...newHome, images: [e.target.value] })}
                placeholder="https://example.com/image.jpg"
                disabled={!!homeImageFile}
              />
              <p className="text-xs text-muted-foreground">
                Alternative zum Datei-Upload
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowAddHomeModal(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleAddHome}
              disabled={!newHome.name || !newHome.description || !newHome.highSeasonRate || !newHome.lowSeasonRate || isUploadingHome}
            >
              {isUploadingHome ? 'Wird hochgeladen...' : 'Unterkunft hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Benutzer zur Whitelist hinzufügen</DialogTitle>
            <DialogDescription>
              Einen neuen Benutzer zur Whitelist hinzufügen. Sie können sich dann mit ihrem Google-Konto anmelden.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="userName">Vollständiger Name</Label>
              <Input
                id="userName"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Vollständigen Namen eingeben"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="userEmail">E-Mail-Adresse</Label>
              <Input
                id="userEmail"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="benutzer@beispiel.com"
              />
              <p className="text-xs text-muted-foreground">
                Muss exakt mit der Google-Konto-E-Mail übereinstimmen
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="userRole">Benutzerrolle</Label>
              <Select
                value={newUser.isAdmin ? 'admin' : newUser.isFamily ? 'owner' : newUser.isFamilyMember ? 'familyMember' : 'guest'}
                onValueChange={(value) => {
                  setNewUser({
                    ...newUser,
                    isAdmin: value === 'admin',
                    isFamily: value === 'admin' || value === 'owner',
                    isFamilyMember: value === 'familyMember',
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Rolle auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (Vollzugriff)</SelectItem>
                  <SelectItem value="owner">Eigentümer (kostenlose Buchungen)</SelectItem>
                  <SelectItem value="familyMember">Familienmitglied (CHF 20/Nacht/Person)</SelectItem>
                  <SelectItem value="guest">Gast (Reguläre Preise)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="referredBy">Empfohlen von (optional)</Label>
              <Select
                value={newUser.referredBy}
                onValueChange={(value) => setNewUser({ ...newUser, referredBy: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Empfehlenden Benutzer auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Niemand (Direkt)</SelectItem>
                  {users
                    .filter((u) => u.isFamily)
                    .map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Unterkunftszugang</Label>
              <p className="text-xs text-muted-foreground">
                Wähle, welche Unterkünfte dieser Benutzer sehen und buchen darf. Ohne Auswahl sind alle Unterkünfte zugänglich.
              </p>
              <div className="space-y-2 rounded-md border p-3">
                {homes.map((home) => {
                  const checked = newUser.allowedHomeIds.includes(home.id);
                  return (
                    <label key={home.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? newUser.allowedHomeIds.filter(id => id !== home.id)
                            : [...newUser.allowedHomeIds, home.id];
                          setNewUser({ ...newUser, allowedHomeIds: next });
                        }}
                        className="h-4 w-4 rounded border-input"
                      />
                      <span className="text-sm">{home.name}</span>
                    </label>
                  );
                })}
                {homes.length === 0 && (
                  <p className="text-xs text-muted-foreground">Keine Unterkünfte vorhanden</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowAddUserModal(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleAddUser}
              disabled={!newUser.name || !newUser.email}
            >
              Benutzer hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Home Confirmation Modal */}
      <Dialog open={showDeleteHomeModal} onOpenChange={setShowDeleteHomeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unterkunft löschen</DialogTitle>
            <DialogDescription>
              Möchtest du "{editingHome?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteHomeModal(false)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteHome}
              disabled={isDeletingHome}
            >
              {isDeletingHome ? 'Wird gelöscht...' : 'Unterkunft löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditUserModal} onOpenChange={setShowEditUserModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>
              Benutzerdetails und Berechtigungen aktualisieren
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editUserName">Vollständiger Name</Label>
              <Input
                id="editUserName"
                value={editingUser?.name || ''}
                onChange={(e) => setEditingUser(editingUser ? { ...editingUser, name: e.target.value } : null)}
                placeholder="Vollständigen Namen eingeben"
              />
            </div>

            <div className="space-y-2">
              <Label>E-Mail-Adresse</Label>
              <p className="text-sm text-muted-foreground">{editingUser?.email}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="editUserRole">Benutzerrolle</Label>
              <Select
                value={editingUser?.isAdmin ? 'admin' : editingUser?.isFamily ? 'owner' : editingUser?.isFamilyMember ? 'familyMember' : 'guest'}
                onValueChange={(value) => {
                  if (editingUser) {
                    setEditingUser({
                      ...editingUser,
                      isAdmin: value === 'admin',
                      isFamily: value === 'admin' || value === 'owner',
                      isFamilyMember: value === 'familyMember',
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Rolle auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (Vollzugriff)</SelectItem>
                  <SelectItem value="owner">Eigentümer (kostenlose Buchungen)</SelectItem>
                  <SelectItem value="familyMember">Familienmitglied (CHF 20/Nacht/Person)</SelectItem>
                  <SelectItem value="guest">Gast (Reguläre Preise)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="editUserStatus">Status</Label>
              <Select
                value={editingUser?.status || 'pending'}
                onValueChange={(value) => {
                  if (editingUser) {
                    setEditingUser({ ...editingUser, status: value });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="approved">Genehmigt</SelectItem>
                  <SelectItem value="blocked">Gesperrt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Unterkunftszugang</Label>
              <p className="text-xs text-muted-foreground">
                Ohne Auswahl sind alle Unterkünfte zugänglich.
              </p>
              <div className="space-y-2 rounded-md border p-3">
                {homes.map((home) => {
                  const checked = editingUser?.allowedHomeIds?.includes(home.id) ?? false;
                  return (
                    <label key={home.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (!editingUser) return;
                          const current = editingUser.allowedHomeIds ?? [];
                          const next = checked
                            ? current.filter(id => id !== home.id)
                            : [...current, home.id];
                          setEditingUser({ ...editingUser, allowedHomeIds: next });
                        }}
                        className="h-4 w-4 rounded border-input"
                      />
                      <span className="text-sm">{home.name}</span>
                    </label>
                  );
                })}
                {homes.length === 0 && (
                  <p className="text-xs text-muted-foreground">Keine Unterkünfte vorhanden</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowEditUserModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateUser}>
              Änderungen speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Availability Management Modal */}
      <Dialog open={showAvailabilityModal} onOpenChange={setShowAvailabilityModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Verfügbarkeit verwalten – {availabilityHome?.name}</DialogTitle>
            <DialogDescription>
              Zeiträume sperren, in denen die Unterkunft nicht buchbar ist
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Add New Blocked Date */}
            <div className="p-4 border rounded-md space-y-4">
              <h4 className="font-medium">Gesperrten Zeitraum hinzufügen</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="blockStartDate">Startdatum</Label>
                  <Input
                    id="blockStartDate"
                    type="date"
                    value={newBlockedDate.startDate}
                    onChange={(e) => setNewBlockedDate({ ...newBlockedDate, startDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="blockEndDate">Enddatum</Label>
                  <Input
                    id="blockEndDate"
                    type="date"
                    value={newBlockedDate.endDate}
                    onChange={(e) => setNewBlockedDate({ ...newBlockedDate, endDate: e.target.value })}
                    min={newBlockedDate.startDate}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="blockReason">Grund (optional)</Label>
                <Input
                  id="blockReason"
                  value={newBlockedDate.reason}
                  onChange={(e) => setNewBlockedDate({ ...newBlockedDate, reason: e.target.value })}
                  placeholder="z.B. Wartung, Eigenbedarf, Renovation"
                />
              </div>
              <Button
                onClick={handleAddBlockedDate}
                disabled={!newBlockedDate.startDate || !newBlockedDate.endDate}
              >
                Zeitraum sperren
              </Button>
            </div>

            {/* Current Blocked Dates */}
            <div className="space-y-2">
              <h4 className="font-medium">Aktuelle Sperrzeiträume</h4>
              {availabilityHome?.availability && availabilityHome.availability.length > 0 ? (
                <div className="space-y-2">
                  {availabilityHome.availability.map((range, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted rounded-md"
                    >
                      <div>
                        <div className="font-medium">
                          {new Date(range.startDate).toLocaleDateString('de-DE')} – {new Date(range.endDate).toLocaleDateString('de-DE')}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {range.reason}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRemoveBlockedDate(index)}
                      >
                        Entfernen
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground p-4 bg-muted rounded-md">
                  Keine Sperrzeiträume. Die Unterkunft ist an allen Daten verfügbar.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvailabilityModal(false)}>
              Schliessen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
